import _ from "lodash";
/** external */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { TextInput, IconButton } from "@toddle-edu/ds-web";
import {
  DeleteOutlined,
  PencilLineOutlined,
  ExpandArrowsTwoOutlined,
  CaretRightFilled,
  CaretDownFilled,
  WarningTriangleOutlined,
  DotsSixVerticalOutlined,
} from "@toddle-edu/ds-icons";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { useI18n } from "Hooks";
/** internal */
import DeleteSectionModal from "Worksheet/components/DeleteSectionModal";
import { scrollIntoView } from "Utils";
import UIModal from "UIComponents/UIModal";
import {
  MODE_TEACHER_EVALUATE,
  MODE_TEACHER_VIEW,
  MODE_STUDENT_EVALUATE,
  MODE_TEACHER_CREATE,
  RESOURCE_TYPE_QUESTION,
  QUESTION_UID_SEPARATOR,
} from "Worksheet/modules/WorksheetConstants";
import {
  updateSectionLabelInCache,
  scrollIntoViewWithOffset,
  isVisualWorksheetElement,
  updateQuestionFieldsInCache,
} from "Worksheet/modules/WorksheetUtils";
import {
  isWorksheetMutationsV2Enabled,
  isWorksheetQueriesV2Enabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  updateActiveQuestion,
  updateAIAssessmentTools,
  updateVisibleQuestionAssistantIds,
} from "Worksheet/modules/WorksheetModules";
/** relative */
import classes from "./SideBarItem.scss";
import MarksComponent from "../MarksComponent";
import TickComponent from "../TickComponent";
import WorksheetToolsHeader from "../../WorksheetToolsHeader";
import ChildOptions from "./ChildOptions";

const styles = {
  textInputStyleRevamp: {
    border: "none",
    height: "22px",
    padding: "0",
  },
};

const SideBarItem = props => {
  const {
    id,
    icon,
    label,
    suffix,
    maxMarks,
    score,
    tier,
    showTickIcon,
    isCompleted,
    isEvaluated,
    showSectionActions,
    mode,
    isQuestion,
    studentId,
    submissionId,
    assessmentTools,
    assessmentToolsEvaluationDetails,
    renderEvaluation,
    submissionStatus,
    questionTypeUid,
    isEvaluationApplicable,
    childOptions,
    activeQuestion,
    index,
    resourceId,
    debouncedUpdateEvaluateScoreMutation,
    debouncedUpdateSectionMutation,
    hasIBGradeOnly,
    questionIndex,
    toggleRef,
    showWarningIcon,
    errorState,
    academicTermId,
    isNotClickable,
    isDragDisabled,
    subLabel,
    rightContent,
    updateActiveQuestion,
    isGeneratingAIQuestions,
    isStaticOption,
    updateScoreElementRating,
    totalMarks,
    updateVisibleQuestionAssistantIds,
    updateAIAssessmentTools,
    parentItemId,
    parentTitleFieldId,
    isQueriesV2Enabled,
  } = props;

  const { t } = useI18n(["common"]);

  const isWorksheetCreateMode = _.isEqual(mode, MODE_TEACHER_CREATE);
  const isSelected = _.isEqual(id, activeQuestion);
  const shouldExpandSectionByDefault = isWorksheetCreateMode && !isQuestion;

  const sideBareItemContainerRef = useRef(null);
  const [expandChildren, setExpandChildren] = useState(
    shouldExpandSectionByDefault
  );

  const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();

  useEffect(() => {
    if (isSelected && sideBareItemContainerRef?.current) {
      if (toggleRef) {
        scrollIntoViewWithOffset({
          element: sideBareItemContainerRef.current,
          block: "start",
          checkForElementInViewPort: true,
          parentElement: toggleRef,
          offset: 100,
        });
      } else {
        scrollIntoView({
          element: sideBareItemContainerRef.current,
          block: "start",
          checkForElementInViewPort: true,
        });
      }
    }
  }, [isSelected]);

  useEffect(() => {
    if (isWorksheetCreateMode) {
      let isChildActive = _.find(childOptions, { id: activeQuestion });
      _.forEach(childOptions, item => {
        const childOptions2 = _.get(item, ["childOptions"]);
        isChildActive = isChildActive
          ? isChildActive
          : _.find(childOptions2, { id: activeQuestion });
      });
      if (isChildActive) {
        setExpandChildren(true);
      }
    }
  }, [activeQuestion]);

  const updateEvaluateScore = ({ scoreVal, userFilter }) => {
    debouncedUpdateEvaluateScoreMutation({
      questionSubmissionId: submissionId,
      studentId,
      newScoreValue: scoreVal,
      userFilter,
      academicTermId,
    });
  };

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isLabelInputMode, setIsLabelInputMode] = useState(false);
  const [showFullScreenEvaluation, setShowFullScreenEvaluation] = useState(
    false
  );

  const isTeacherSubmissionView = _.includes(
    [MODE_TEACHER_VIEW, MODE_TEACHER_EVALUATE],
    mode
  );

  const _onOptionClick = () => {
    // Force any active elements to blur first
    if (
      document.activeElement &&
      document.activeElement instanceof HTMLElement &&
      isWorksheetCreateMode
    ) {
      document.activeElement.blur();
    }

    updateVisibleQuestionAssistantIds([]);
    updateAIAssessmentTools({ isOpen: false });
    updateActiveQuestion(id);
  };

  const toggleDeleteDialog = () => {
    setShowDeleteDialog(prev => !prev);
  };

  const editLabelInput = () => {
    setIsLabelInputMode(true);
  };

  const updateSectionHeader = e => {
    const newLabel = _.get(e, "target.value", "");
    if (isQueriesV2Enabled) {
      updateQuestionFieldsInCache({
        fields: [
          {
            id: parentTitleFieldId,
            value: newLabel,
          },
        ],
        isQueriesV2Enabled,
      });
    } else {
      //WORKSHEET_RESTRUCTURING:Remove else part
      updateSectionLabelInCache({ id, label: newLabel });
    }

    if (isMutationsV2Enabled) {
      debouncedUpdateSectionMutation({
        questionId: parentItemId,
        fields: {
          id: parentTitleFieldId,
          value: newLabel,
        },
      });
    } else {
      debouncedUpdateSectionMutation({
        id,
        label: newLabel,
      });
    }
  };
  const toggleFullScreenEvaluation = () => {
    setShowFullScreenEvaluation(!showFullScreenEvaluation);
  };

  // * Update the isDraggable condition to only check for mode and tier
  const shouldShowDragHandle =
    mode == MODE_TEACHER_CREATE && tier <= 2 && !isDragDisabled;

  // * Separate variable for actual draggability - disabled during AI question generation
  const isDraggable = shouldShowDragHandle && !isGeneratingAIQuestions;

  const containerClasses = classNames(classes.container, {
    [classes.clickableContainer]: tier > 1,
    [classes.draggableContainer]: shouldShowDragHandle,
    [classes.sidebarItemRevampedContainer]: isWorksheetCreateMode,
    [classes.sidebarItemRevampedContainerHover]:
      isWorksheetCreateMode && !isLabelInputMode,
    [classes.sidebarItemInputContainer]: isLabelInputMode,
    [classes.sidebarItemSelectedContainer]: isSelected,
  });

  const shouldHaveMaxWidth = !_.includes(
    [MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW],
    mode
  );

  const labelClasses = classNames(classes.label, classes.fitContentWidth, {
    ["text-body-1"]: tier == 1 && !isWorksheetCreateMode,
    [classes.questionLabel]: tier > 1 && !isWorksheetCreateMode,
    ["text-body"]: isWorksheetCreateMode,
    [classes.labelActive]: isSelected || showSectionActions,
    [classes.labelTier1]: tier == 1 && shouldHaveMaxWidth,
    [classes.revampQuestionLabel]: tier > 1 && isWorksheetCreateMode,
    [classes.breakAllWord]: isWorksheetCreateMode,
    "text-label": tier === 1,
    [classes.labelTier1Revamp]: tier === 1,
  });

  const evaluationContainerClasses = classNames({
    [classes.evaluationContainer]: showFullScreenEvaluation,
    [classes.evaluationContainerIBGrade]:
      hasIBGradeOnly && !showFullScreenEvaluation,
    [classes.evaluationIBGradeModalView]:
      hasIBGradeOnly && showFullScreenEvaluation,
  });

  const expandArrowIconClasses = classNames({
    [classes.expandArrowIcon]: true,
    [classes.expandArrowIconIBGrade]: hasIBGradeOnly,
  });

  const suffixClasses = classNames("text-body-3", classes.suffix);

  const leftContentClasses = classNames(
    classes.leftContent,
    classes.fullWidth,
    {
      [classes.leftContentTier2]: tier == 2 && !isWorksheetCreateMode,
      [classes.leftContentTier3]: tier == 3,
      [classes.hiddenL]: mode == MODE_TEACHER_CREATE && tier !== 3,
    }
  );

  const studentSubmitted = !_.includes(
    ["REQUEST_RESUBMISSION", "SUBMISSION_PENDING"],
    submissionStatus
  );

  const showAssessmentTools =
    isSelected && isQuestion && !_.isNil(submissionId) && studentSubmitted;

  const extraParamsObj = {
    studentAssignmentId: assessmentToolsEvaluationDetails?.parentId,
    assessmentTools,
    ibPYPElementRatings: assessmentToolsEvaluationDetails?.ibPYPElementRatings,
    elementRemarks: assessmentToolsEvaluationDetails?.elementRemarks,
    assignmentDetailsVariables: {},
    questionFields: _.get(
      assessmentToolsEvaluationDetails,
      "questionFields",
      []
    ),
  };

  const shouldRenderMarksComponent =
    !_.isNil(maxMarks) &&
    _.includes([MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW], mode);
  const shouldRenderSuffix = !_.isNil(suffix);

  const evaluatedScore =
    mode == MODE_STUDENT_EVALUATE && !isVisualWorksheetElement(questionTypeUid)
      ? !_.isNil(score)
        ? score
        : "0"
      : null;

  const wrapperContainerClasses = classNames(classes.wrapperContainer, {
    [classes.revampWrapperContainer]: isWorksheetCreateMode && !isStaticOption,
    [classes.fullWidth]: isWorksheetCreateMode,
  });

  const subLabelClasses = classNames(classes.subLabel, "text-body-3");

  const questionTypeIconClasses = classNames(classes.icon);

  const caretIconSize =
    isWorksheetCreateMode && _.isEqual(tier, 1) ? "xx-small" : "xxx-small";

  const sideBarItemContainerClasses = classNames(classes.sideBarItemContainer, {
    [classes.revampSideBarItemContainer]:
      isWorksheetCreateMode && isDragDisabled && !isNotClickable,
    [classes.sidebarItemSeparatorContainer]:
      questionTypeUid === QUESTION_UID_SEPARATOR,
  });

  const getSideBarItem = (provided, snapshot) => {
    const isDragging = _.get(snapshot, "isDragging");

    const dragHandleClasses = classNames(classes.dragIconRevamp, {
      [classes.dragIconVisible]: shouldShowDragHandle,
      [classes.showDragIcon]: isDragging,
      [classes.dragIconDisabled]: isGeneratingAIQuestions,
    });

    return (
      <div
        className={sideBarItemContainerClasses}
        ref={isDraggable ? provided?.innerRef : null}
        {...(isDraggable ? provided?.draggableProps : {})}
      >
        <Droppable
          droppableId={isMutationsV2Enabled ? parentItemId ?? id : resourceId} // Use parentItemId for sections and id for questions
          type={tier == 1 ? RESOURCE_TYPE_QUESTION : null}
        >
          {droppableProvided => (
            <div
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              <div
                onClick={isNotClickable ? _.noop : _onOptionClick}
                id={tier > 1 ? "active-question-toggle" : ""}
                ref={sideBareItemContainerRef}
                {...provided?.dragHandleProps}
                className={containerClasses}
                data-test-id="worksheet-sidebarItem-item"
                aria-label={`sidebarItemLabel-${
                  _.get(label, "props.children") ||
                  _.get(label, "0.props.children") ||
                  label
                }`}
                role="button"
                tabIndex={0}
                onKeyDown={event => {
                  if (event.key === "Enter" && !isNotClickable) {
                    _onOptionClick();
                  }
                }}
              >
                {isLabelInputMode ? (
                  <div className={classes.input}>
                    <TextInput
                      size="medium"
                      value={label}
                      onChange={updateSectionHeader}
                      autoFocus
                      onBlur={() => setIsLabelInputMode(false)}
                      style={styles.textInputStyleRevamp}
                      testId="worksheet-sidebarItem-updateSection"
                    />
                  </div>
                ) : (
                  <div className={wrapperContainerClasses}>
                    <div className={leftContentClasses}>
                      {/* DRAG HANDLE TO REORDER QUESTIONS */}
                      {shouldShowDragHandle ? (
                        <div
                          className={dragHandleClasses}
                          data-test-id="worksheet-sidebarItem-dragHandle"
                        >
                          <DotsSixVerticalOutlined
                            size={"xxx-small"}
                            className={classes.iconSecondary}
                          />
                        </div>
                      ) : null}

                      {/* EXPAND COLLAPSE MULTIPART/SECTION */}
                      {!_.isEmpty(childOptions) && shouldShowDragHandle ? (
                        <div
                          tabIndex={0}
                          onClick={() => {
                            setExpandChildren(!expandChildren);
                          }}
                          className={classes.caretIcon}
                          onKeyDown={event => {
                            if (event.key === "Enter") {
                              setExpandChildren(!expandChildren);
                            }
                          }}
                          aria-label={
                            expandChildren
                              ? t("common:collapse")
                              : t("common:expand")
                          }
                          data-test-id="worksheet-sidebarItem-itemExpandCollapse"
                        >
                          {expandChildren ? (
                            <CaretDownFilled
                              variant="subtle"
                              size={caretIconSize}
                              className={classes.iconSecondary}
                            />
                          ) : (
                            <CaretRightFilled
                              variant="subtle"
                              size={caretIconSize}
                              className={classes.iconSecondary}
                            />
                          )}
                        </div>
                      ) : null}

                      {React.isValidElement(icon) ? (
                        <div className={questionTypeIconClasses}>{icon}</div>
                      ) : null}
                      {_.isNil(label) ? null : (
                        <div className={classes.fullWidth}>
                          <div className={labelClasses} dir="auto">
                            {label}
                          </div>
                          {_.isNil(subLabel) ? null : (
                            <div className={subLabelClasses} dir="auto">
                              {subLabel}
                            </div>
                          )}
                        </div>
                      )}
                      {showSectionActions ? (
                        <div className={classes.sectionActions}>
                          <IconButton
                            dsVersion="2.0"
                            type="plain"
                            variant="neutral"
                            size="x-small"
                            _legacy_size="xx-small"
                            _legacy_variant="plain"
                            icon={<PencilLineOutlined />}
                            onClick={editLabelInput}
                            testId="worksheet-sidebarItem-editSection"
                            aria-label={t("unitPlan:edit_section_title")}
                          />
                          <IconButton
                            dsVersion="2.0"
                            type="plain"
                            variant="neutral"
                            size="x-small"
                            _legacy_size="xx-small"
                            _legacy_variant="plain"
                            icon={<DeleteOutlined />}
                            onClick={toggleDeleteDialog}
                            testId="worksheet-sidebarItem-deleteSection"
                            disabled={isGeneratingAIQuestions}
                            aria-label={t("common:delete_section")}
                          />
                        </div>
                      ) : null}
                    </div>
                    <div className={classes.rightContent}>
                      {showWarningIcon ? (
                        <div className={classes.warningIcon}>
                          <WarningTriangleOutlined
                            variant={"warning"}
                            size={"xxx-small"}
                          />
                        </div>
                      ) : null}
                      {shouldRenderMarksComponent ? (
                        <MarksComponent
                          maxMarks={maxMarks}
                          score={score}
                          isQuestion={isQuestion}
                          questionSubmissionId={submissionId}
                          updateEvaluateScore={updateEvaluateScore}
                          mode={mode}
                          questionTypeUid={questionTypeUid}
                          isEvaluationApplicable={isEvaluationApplicable}
                          studentNotSubmitted={!studentSubmitted}
                          updateScoreElementRating={updateScoreElementRating}
                          totalMarks={totalMarks}
                        />
                      ) : (
                        <div className={classes.marks}>
                          {!_.isNil(evaluatedScore) ? (
                            <>
                              <div className="text-label-3">
                                {evaluatedScore}
                              </div>
                              <div className={suffixClasses}>/</div>
                            </>
                          ) : null}
                          {shouldRenderSuffix ? (
                            <div className={suffixClasses}>{suffix}</div>
                          ) : null}
                        </div>
                      )}
                      {showTickIcon ? (
                        <TickComponent
                          isCompleted={
                            isTeacherSubmissionView ? isEvaluated : isCompleted
                          }
                        />
                      ) : null}
                    </div>
                    {showDeleteDialog ? (
                      <DeleteSectionModal
                        id={id}
                        label={label}
                        toggleModal={toggleDeleteDialog}
                        mode={mode}
                        parentItemId={parentItemId}
                      />
                    ) : null}
                    {_.isNil(rightContent) ? null : rightContent}
                  </div>
                )}
                {showAssessmentTools && renderEvaluation ? (
                  <div className={classes.evaluationDetails}>
                    <div className={expandArrowIconClasses}>
                      <IconButton
                        dsVersion="2.0"
                        type="plain"
                        variant="neutral"
                        size="medium"
                        _legacy_size="small"
                        _legacy_variant="plain"
                        icon={<ExpandArrowsTwoOutlined />}
                        onClick={toggleFullScreenEvaluation}
                        testId="worksheet-sidebarItem-fullScreenToggle"
                      />
                    </div>
                    <div className={evaluationContainerClasses}>
                      {renderEvaluation({
                        isSplitView: true,
                        assessmentToolsCustomObj: assessmentTools,
                        toolResponsesDataCustomObj:
                          assessmentToolsEvaluationDetails.toolEvaluationResponses,
                        assessmentToolsEvaluationDetails: extraParamsObj,
                      })}
                    </div>
                  </div>
                ) : null}
                {showFullScreenEvaluation ? (
                  <UIModal
                    isOpen={true}
                    modalContent={classes.modalContent}
                    overlayContainer={classes.overlayContainer}
                    onRequestClose={toggleFullScreenEvaluation}
                    testId="worksheet-sidebarItem-closeEvaluationModal"
                  >
                    <div className={classes.modalContainer}>
                      <WorksheetToolsHeader
                        questionIndex={questionIndex}
                        label={label}
                        toggleFullScreenEvaluation={toggleFullScreenEvaluation}
                        showCollapseIcon={true}
                      />
                      <div className={evaluationContainerClasses}>
                        {renderEvaluation({
                          isSplitView: false,
                          assessmentToolsCustomObj: assessmentTools,
                          toolResponsesDataCustomObj:
                            assessmentToolsEvaluationDetails.toolEvaluationResponses,
                          assessmentToolsEvaluationDetails: extraParamsObj,
                          isFullScreenEvaluation: true,
                        })}
                      </div>
                    </div>
                  </UIModal>
                ) : null}
              </div>
              {!_.isEmpty(childOptions)
                ? !expandChildren && mode == MODE_TEACHER_CREATE
                  ? null // In teacher create mode if expand children in false, then no child elements would be shown
                  : _.map(childOptions, (childOption, childIndex) => {
                      return (
                        <ChildOptions
                          key={_.get(childOption, "id")}
                          childOption={childOption}
                          mode={mode}
                          errorState={errorState}
                          toggleRef={toggleRef}
                          studentId={studentId}
                          renderEvaluation={renderEvaluation}
                          isEvaluationApplicable={isEvaluationApplicable}
                          submissionStatus={submissionStatus}
                          childIndex={childIndex}
                          debouncedUpdateEvaluateScoreMutation={
                            debouncedUpdateEvaluateScoreMutation
                          }
                          debouncedUpdateSectionMutation={
                            debouncedUpdateSectionMutation
                          }
                          academicTermId={academicTermId}
                          updateScoreElementRating={updateScoreElementRating}
                          totalMarks={totalMarks}
                          {...childOption}
                        />
                      );
                    })
                : null}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  return isDraggable ? (
    <Draggable
      key={id}
      draggableId={isMutationsV2Enabled ? parentItemId ?? id : resourceId} // Use parentItemId for sections and id for questions
      index={index}
      isDragDisabled={isGeneratingAIQuestions}
    >
      {(provided, snapshot) => getSideBarItem(provided, snapshot)}
    </Draggable>
  ) : (
    getSideBarItem()
  );
};

SideBarItem.propTypes = {
  id: PropTypes.string.isRequired,
  icon: PropTypes.element,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  tier: PropTypes.number,
  suffix: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.element,
  ]),
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  isCompleted: PropTypes.bool,
  showDeleteSection: PropTypes.bool,
  assessmentTools: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  assessmentToolsEvaluationDetails: PropTypes.object,
  renderEvaluation: PropTypes.func,
  questionTypeUid: PropTypes.string,
  isEvaluationApplicable: PropTypes.bool,
  hasIBGradeOnly: PropTypes.bool,
  submissionStatus: PropTypes.string,
  questionIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isDraggable: PropTypes.bool,
  childOptions: PropTypes.array,
  index: PropTypes.number,
  resourceId: PropTypes.string,
  debouncedUpdateEvaluateScoreMutation: PropTypes.func,
  debouncedUpdateSectionMutation: PropTypes.func,
  subLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  isDragDisabled: PropTypes.bool,
  isStaticOption: PropTypes.bool,
  updateScoreElementRating: PropTypes.func,
  totalMarks: PropTypes.number,
  parentItemId: PropTypes.string,
  parentTitleFieldId: PropTypes.string,
};

SideBarItem.defaultProps = {
  isSelected: false,
  onClick: _.noop,
  showDeleteSection: false,
  isDraggable: false,
  hasIBGradeOnly: false,
  showWarningIcon: false,
  isDragDisabled: false,
  isStaticOption: false,
  updateScoreElementRating: _.noop,
  parentItemId: null,
  parentTitleFieldId: null,
};

const mapStateToProps = (state, ownProps) => {
  const { isFromToddleLibrary } = ownProps;
  const activeQuestion = _.get(state, "worksheet.activeQuestion");
  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  // This is used to determine if the new query structure should be applied
  // V2 queries should be used (feature flag enabled AND not in Toddle Library)
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled(isFromToddleLibrary);
  return {
    activeQuestion,
    isGeneratingAIQuestions,
    isQueriesV2Enabled,
  };
};

const mapActionCreators = {
  updateActiveQuestion,
  updateVisibleQuestionAssistantIds,
  updateAIAssessmentTools,
};

export default connect(mapStateToProps, mapActionCreators)(SideBarItem);
